/**
 * metrics.js
 * @author: Bernhard Wolkerstorfer
 *
 * Description: Metrics
 *
 * Copyright (c) 2012 Amazon.com, Inc. All rights reserved.
 */

import {MetricsBody} from "./metrics-body";
import {emitMetrics} from "../../src-worker/s2k-request";

/**
 * Timer of Time metrics
 * @typedef {{sum: number, count: number, start: number}} Timer
 */

const MetricsNames = {
    // -------------------------------- Counter --------------------------------
    // User click sign-in button in popup sign-in page
    "pop-unauth-sign-in-btn-click" : "PopupSignIn:SignInBtn:Click",
    // User click forgot password button in popup sign-in page
    "pop-unauth-psw-btn-click" : "PopupSignIn:PasswordBtn:Click",
    // User click Create account button in popup sign-in page
    "pop-unauth-reg-btn-click" : "PopupSignIn:RegisterBtn:Click",

    // User click continue in country picker page
    "pop-country-picker-continue-click" : "PopupCountryPicker:ContinueBtn:Click",
    // User click country list entry
    "pop-country-single-entry-click" : "PopupCountrySingle:Entry:Click",
    // User select current country
    "pop-country-list-cur-click" : "PopupCountryList:Current:Click",
    // User select other country
    "pop-country-list-other-click" : "PopupCountryList:Other:Click",

    // User click send button in main page
    "pop-main-send-click" : "PopupMain:SendBtn:Click",
    // User use shortcut of send
    "pop-main-send-shortcut" : "PopupMain:SendBtn:ShortCut",
    // User click preview button in main page
    "pop-main-preview-click" : "PopupMain:PreviewBtn:Click",
    // User use shortcut of preview
    "pop-main-preview-shortcut" : "PopupMain:PreviewBtn:ShortCut",
    // User click send-selection button in main page
    "pop-main-send-sel-click" : "PopupMain:SelectBtn:Click",
    // User click history button in main page
    "pop-main-history-click" : "PopupMain:HistoryBtn:Click",
    // User click settings button in main page
    "pop-main-setting-click" : "PopupMain:SettingsBtn:Click",
    // User click feedback button in main page
    "pop-main-feedback-click" : "PopupMain:FeedbackBtn:Click",

    // User click clear button in history page
    "history-clear-click" : "History:ClearBtn:Click",
    // User click close button in history page
    "history-close-click" : "History:CloseBtn:Click",
    // User click delete button in specific item in history page
    "history-delete-click" : "History:DeleteBtn:Click",
    // User click specific item in history page
    "history-item-click" : "History:item:Click",

    // User open archive in settings page
    "pop-setting-arch-on" : "PopupSetting:Archive:On",
    // User off archive in settings page
    "pop-setting-arch-off" : "PopupSetting:Archive:Off",
    // User select a device in settings page
    "pop-setting-device-check" : "PopupSetting:Device:Check",
    // User unselect a device in settings page
    "pop-setting-device-uncheck" : "PopupSetting:Device:UnCheck",
    // User click logout in settings page
    "pop-setting-logout-click" : "PopupSetting:LogOutBtn:Click",

    // User click star of n in popup feedback (need to add the specific star number as suffix)
    "pop-feedback-star-click" : "PopupFeedback:Star:",
    // User submit with star of n in popup feedback (need to add the specific star number as suffix)
    "pop-feedback-submit-click" : "PopupFeedback:SubmitBtn:",
    // User submit with input is not null in popup feedback
    "pop-feedback-submit-input" : "PopupFeedback:SubmitBtn:Input",
    // User submit with input is null in popup feedback
    "pop-feedback-submit-empty" : "PopupFeedback:SubmitBtn:Empty",

    // Title length is reach limit in preview page
    "preview-length-alert-title" : "Preview:LengthAlert:Title",
    // Author length is reach limit in preview page
    "preview-length-alert-author" : "Preview:LengthAlert:Author",
    // User click Send button with tile edited in preview page
    "preview-send-click-title" : "Preview:SendBtn:Title",
    // User click Send button with author edited in preview page
    "preview-send-click-author" : "Preview:SendBtn:Author",
    // User click Send button in preview page
    "preview-send-click" : "Preview:SendBtn:Click",
    // User click Feedback button in preview page
    "preview-feedback-click" : "Preview:FeedbackBtn:Click",
    // User click Back button in preview page
    "preview-back-click" : "Preview:BackBtn:Click",

    // User click star of n in preview feedback (need to add the specific star number as suffix)
    "preview-feedback-star-click" : "PreviewFeedback:Star:",
    // User submit with star of n in preview feedback (need to add the specific star number as suffix)
    "preview-feedback-submit-click" : "PreviewFeedback:SubmitBtn:",
    // User submit with input is not null in preview feedback
    "preview-feedback-submit-input" : "PreviewFeedback:SubmitBtn:Input",
    // User submit with input is null in preview feedback
    "preview-feedback-submit-empty" : "PreviewFeedback:SubmitBtn:Empty",

    // The counter of user start extract web content
    "extract-count" : "Extraction:Count",
    // The error count of web extraction
    "extract-error" : "Extraction:Error",
    // The time-out count of web extraction
    "extract-timeout" : "Extraction:Timeout",
    // The success count of web extraction
    "extract-success" : "Extraction:Success",

    // The counter of user call init api (need to add OCS/Preview as prefix to indicate the way)
    "call-init-count" : ":CallInit:Count",
    // The error count of user call init api (need to add OCS/Preview as prefix to indicate the way)
    "call-init-error" : ":CallInit:Error",
    // The success count of user call init api(need to add OCS/Preview as prefix to indicate the way)
    "call-init-success" : ":CallInit:Success",

    // The counter of user upload file to s3 (need to add OCS/Preview as prefix to indicate the way)
    "upload-count" : ":UploadS3:Count",
    // The error count of user upload file to s3 (need to add OCS/Preview as prefix to indicate the way)
    "upload-error" : ":UploadS3:Error",
    // The success count of user upload file to s3 (need to add OCS/Preview as prefix to indicate the way)
    "upload-success" : ":UploadS3:Success",

    // The counter of trigger STK workflow (need to add OCS/Preview as prefix to indicate the way)
    "stk-count" : ":STK:Count",
    // The error count of trigger STK workflow (need to add OCS/Preview as prefix to indicate the way)
    "stk-error" : ":STK:Error",
    // The success count of trigger STK workflow (need to add OCS/Preview as prefix to indicate the way)
    "stk-success" : ":STK:Success",

    // The counter of device list request
    "deviceList-count" : "DeviceList:Count",
    // The error count of device list request
    "deviceList-error" : "DeviceList:Error",
    // The success count of device list request
    "deviceList-success" : "DeviceList:Success",

    // -------------------------------- Timer --------------------------------
    // The time cost of web extraction (need to add OCS/Preview as prefix to indicate the way)
    "extract-time" : "Extraction:Time",

    // The time cost of user call init api (need to add OCS/Preview as prefix to indicate the way)
    "call-init-time" : ":CallInit:Time",

    // The time cost of user upload file to s3 (need to add OCS/Preview as prefix to indicate the way)
    "upload-time" : ":UploadS3:Time",

    // The time cost of trigger STK workflow (need to add OCS/Preview as prefix to indicate the way)
    "stk-time" : ":STK:Time",

    // The time cost of device list request
    "deviceList-time" : "DeviceList:Time",
};

class Metrics {
    // ######## Constructor ########
    constructor() {
        /**
         *
         * @type {Record<Metrics['NAMES'], number>}
         */
        this.counters = {};
        /**
         *
         * @type {Record<Metrics['NAMES'], Timer>}
         */
        this.timers = {};

    }

    // ######## Methods ########
    /**
     * Initialize metrics session.
     */
    init() {
        this.counters = {};
        this.timers = {};
        this.loadMetrics();
    }

    /**
     * Terminate a metrics session.
     * @param {function()} callback Callback
     */
    terminate(callback) {
        // Create metrics list
        let metricsList = [];
        // Add timer metrics data.
        for (let name in this.timers) {
            let timer = this.timers[name];
            if (timer.count > 0) {
                metricsList.push(new MetricsBody(name, "TIME", Math.floor(timer.sum / timer.count)));
            }
        }

        // Add counter metrics data.
        for (let name in this.counters) {
            let counter = this.counters[name];
            if (counter ) {
                metricsList.push(new MetricsBody(name, "COUNTER", counter));
            }
        }

        // Trigger service request to store metrics.
        emitMetrics(metricsList).then(function (success) {
            if (success) {
                this.clearMetrics();
                // Reset session data
                this.init();
            }
            if (callback ) {
                callback();
            }
        }.bind(this));
    }

    /**
     * Start a timer session.
     * @param {string} timerName Key of time metrics
     */
    startTimer(timerName) {
        if (timerName ) {
            let timer = this.timers[timerName];
            // Check if already have timer
            if (timer) {
                // Since we do not support send before last sending progress finished in one page,
                // so the start of duplicate `start timer` will be override.
                // That is, we will treat the last progress end in an unexpected way, and reset timer metrics.
                timer.start = Date.now();
            } else if (!timer) {
                // If not have the timer, then create a new one
                this.timers[timerName] = {
                    sum: 0,
                    count: 0,
                    start: Date.now()
                };
            }
        }
    }

    /**
     * Stop a timer session.
     * @param {string} timerName Name of time metrics
     */
    stopTimer(timerName) {
        if (timerName ) {
            // Get timer
            let timer = this.timers[timerName];
            if (timer  && timer.start ) {
                timer.sum += Date.now() - timer.start;
                timer.count++;
                timer.start = undefined;
            }
            this.saveMetrics();
        }
    }

    /**
     * Increase counters.
     * This method uses a variable parameter list.
     * @param {string} counterName
     */
    count(counterName) {
        if (counterName ) {
            if (this.counters[counterName] ) {
                // Increase counter if it is already exist
                this.counters[counterName]++;
            } else {
                // Create new counter if it is not exist
                this.counters[counterName] = 1;
            }
            this.saveMetrics();
        }
    }

    /**
     * Serialize this instance so it can be sent down an extension message channel
     * @returns {{
     *     counters: MetricsBody[],
     *     timers: MetricsBody[]
     * }}
     */
    serialize() {
        return JSON.parse(JSON.stringify({
            counters: this.counters,
            timers: this.timers
        }));
    }

    /**
     * Merges a serialized metrics object into this one, overwriting any existing counters in this
     * instance with incoming values
     * @param {ReturnType<Metrics['serialize']>} serialized
     */
    deserialize(serialized) {
        this.counters = {
            ...this.counters,
            ...serialized.counters
        };

        this.timers = {
            ...this.timers,
            ...serialized.timers
        };
    }

    /**
     * Store all metrics that haven't reported to cache
     */
    saveMetrics() {
        let metricsString = JSON.stringify(this.serialize());
        chrome.storage.local.set({ "unreported-Metrics": metricsString }, function() {});
    }

    /**
     * Read all metrics that haven't reported from cache
     */
    loadMetrics() {
        chrome.storage.local.get(["unreported-Metrics"], function (result) {
            if (result && result['unreported-Metrics']) {
                const metricsData = JSON.parse(result['unreported-Metrics']);
                this.deserialize(metricsData);
            }
            chrome.storage.local.set({ "unreported-Metrics": "" }, function() {});
        }.bind(this));
    }

    /**
     * Clear all metrics in cache
     */
    clearMetrics() {
        chrome.storage.local.set({ "unreported-Metrics": "" }, function() {});
    }
}

export {MetricsNames, Metrics};
